import React, { ChangeEvent, FC, useCallback, useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import { FloatingLabelInput } from 'components/floating-label-fields';
import { brandFont } from 'components/layout/brand-font';
import {
  GetLoyaltyCardsDocument,
  ILoyaltyCard,
  useAddVoucherMutation,
  useMergeLoyaltyCardsMutation,
} from 'generated/rbi-graphql';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { primitive } from 'styles/constants/primitives';
import { parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLErrorCodes } from 'utils/errors/types';
import { mutationDidSucceed } from 'utils/graphql';

import { cleanLoyaltyCardId, formatLoyaltyCardId } from '../utils';

const Anchor = styled.a`
  &:target {
    position: relative;
    top: -360px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 0 ${primitive.$spacing7} 0;
  text-align: center;
`;

const InputInner = styled.div`
  width: min(400px, 100%);
  margin-top: ${primitive.$spacing7};
`;

const NormalText = styled.p`
  font: ${brandFont.headerThree};
  line-height: 1.56;
  margin: ${primitive.$spacing4};
  text-align: center;
`;

const SuccessHeading = styled.h4`
  color: ${p => p.theme.token('text-default')};
  font-size: 1.5rem;
  line-height: 1.56;
  margin: ${primitive.$spacing4} ${primitive.$spacing4} 0;
  text-align: center;
`;

const SuccessText = styled(NormalText)`
  color: ${p => p.theme.token('text-default')};
  margin: ${primitive.$spacing2} ${primitive.$spacing4} ${primitive.$spacing4};
`;

const ErrorText = styled(NormalText)`
  color: ${p => p.theme.token('text-error')};
`;

const StrongText = styled.strong`
  font-weight: 800;
`;

const ErrorAnchor = styled.a`
  color: ${p => p.theme.token('text-error')};
`;

const CARD_ID_PLACEHOLDER = 'XXXX-XXXX-XXXX';

export type IRegistrationProps = {
  activeCard: ILoyaltyCard | null;
  buttonText?: string;
  isVoucher?: boolean;
  enableVoucher?: boolean;
};

enum InputId {
  PROMO = 'promoCodeInput',
  LOYALTY = 'loyaltyCardInput',
}

const Registration: FC<IRegistrationProps> = ({
  activeCard,
  buttonText,
  isVoucher,
  enableVoucher,
}) => {
  const { formatMessage, locale } = useIntl();
  const { logEvent } = useMParticleContext();

  const logRegistrationEvent = useCallback(
    (success: boolean, message?: string) => {
      const eventName = isVoucher
        ? CustomEventNames.PROMO_CARD_REGISTERED
        : CustomEventNames.PHYSICAL_CARD_REGISTERED;

      logEvent(eventName, EventTypes.UserContent, {
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
      });
    },
    [isVoucher, logEvent]
  );

  const [addVoucherMutation, addVoucherMutationResult] = useAddVoucherMutation();
  const { loading: addVoucherLoading, error: addVoucherError } = addVoucherMutationResult;
  const addVoucherSucceeded = mutationDidSucceed(addVoucherMutationResult);
  const addVoucherErrorCodes = addVoucherError ? parseGraphQLErrorCodes(addVoucherError) : null;
  const addVoucherErrorMessage = {
    [GraphQLErrorCodes.VOUCHER_NOT_FOUND]: { id: 'voucherNotFound' },
    [GraphQLErrorCodes.VOUCHER_USED]: { id: 'voucherUsed' },
    [GraphQLErrorCodes.VOUCHER_ERROR]: { id: 'somethingWentWrong' },
  }[addVoucherErrorCodes?.[0]?.errorCode ?? ''] || { id: 'somethingWentWrong' };

  const [fireMergeLoyaltyCardsMutation, mutationResult] = useMergeLoyaltyCardsMutation({
    refetchQueries: [{ query: GetLoyaltyCardsDocument }],
  });
  const { error: mergeLoyaltyCardsError, loading: mergeLoyaltyCardsLoading } = mutationResult;

  const errorCodes = mergeLoyaltyCardsError ? parseGraphQLErrorCodes(mergeLoyaltyCardsError) : null;
  const errorMessage =
    errorCodes && errorCodes[0].errorCode === GraphQLErrorCodes.LOYALTY_CARD_ALREADY_LINKED_ERROR
      ? { id: 'loyaltyCardAlreadyLinked' }
      : { id: 'loyaltyCardMergeFailed' };

  const mergeLoyaltySucceeded = mutationDidSucceed(mutationResult);

  const requestFailedErrorMessage = formatMessage(errorMessage, {
    link: (
      <ErrorAnchor
        href={`https://global.knowledgeforce.com/casemanagement/form/timhortons?lang=${locale}&app-version=N/A`}
      >
        {formatMessage({ id: 'chatWithUs' })}
      </ErrorAnchor>
    ),
  });

  const [physicalCardId, setPhysicalCardId] = useState('');
  const [formattedPhysicalCardId, setFormattedPhysicalCardId] = useState('');
  const [physicalCardIdValid, setPhysicalCardIdValid] = useState(true);
  const [showVoucherErrorText, setShowVoucherErrorText] = useState(false);

  const formatCardId = useCallback(cardId => {
    // remove all non alpha numeric characters
    const rawValue = cleanLoyaltyCardId(cardId);
    // adds hyphen every 4 characters
    const formattedValue = formatLoyaltyCardId(rawValue);
    return [rawValue, formattedValue];
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const cardId = event.target.value.toUpperCase();
      const [rawCardIdValue, formattedCardIdValue] = formatCardId(cardId);

      setPhysicalCardIdValid(rawCardIdValue.length === 12);
      setFormattedPhysicalCardId(formattedCardIdValue);
      setShowVoucherErrorText(false);
      setPhysicalCardId(cardId);
    },
    [setPhysicalCardIdValid, setPhysicalCardId, formatCardId]
  );

  const { cardId: activeCardId = '' } = activeCard || {};
  const handleMergeLoyaltyCards = useCallback(async () => {
    if (!physicalCardIdValid && isVoucher) {
      setShowVoucherErrorText(true);
      return;
    }

    if (!physicalCardId || !physicalCardIdValid || !activeCardId) {
      return;
    }

    const cleanedLoyaltyCardId = cleanLoyaltyCardId(physicalCardId);
    try {
      if (isVoucher) {
        await addVoucherMutation({
          variables: { voucherId: cleanedLoyaltyCardId },
        });
      } else {
        await fireMergeLoyaltyCardsMutation({
          variables: {
            cardId: cleanedLoyaltyCardId,
            existingCardId: activeCardId,
          },
        });
      }
      logRegistrationEvent(true);
    } catch (error) {
      logRegistrationEvent(false, error.message);
    }
  }, [
    physicalCardId,
    physicalCardIdValid,
    activeCardId,
    isVoucher,
    addVoucherMutation,
    fireMergeLoyaltyCardsMutation,
    logRegistrationEvent,
  ]);

  useEffectOnUpdates(() => {
    if (mergeLoyaltySucceeded) {
      setPhysicalCardId('');
      setPhysicalCardIdValid(true);
    }
  }, [mergeLoyaltySucceeded]);

  return (
    <Container>
      <InputInner>
        <Anchor id={isVoucher ? InputId.PROMO : InputId.LOYALTY} />
        <FloatingLabelInput
          label={formatMessage({ id: isVoucher ? 'promoCodeLabel' : 'enterCardNumber' })}
          placeholder={CARD_ID_PLACEHOLDER}
          value={formattedPhysicalCardId}
          onChange={handleChange}
        />
      </InputInner>
      {mergeLoyaltySucceeded && (
        <>
          <SuccessHeading>{formatMessage({ id: 'yay' })}</SuccessHeading>
          <SuccessText>
            {formatMessage({ id: 'yourCardHasBeenSuccessfullyRegistered' })}
          </SuccessText>
        </>
      )}
      {addVoucherSucceeded && (
        <SuccessText>
          {formatMessage(
            { id: 'promoCodeApplied' },
            {
              page: <StrongText>{formatMessage({ id: 'promoCodeAppliedPage' })}</StrongText>,
            }
          )}
        </SuccessText>
      )}
      {isVoucher
        ? showVoucherErrorText && <ErrorText>{formatMessage({ id: 'voucherCodeError' })}</ErrorText>
        : !physicalCardIdValid && (
            <ErrorText>{formatMessage({ id: 'loyaltyCardValidationError' })}</ErrorText>
          )}
      {mergeLoyaltyCardsError && (
        <>
          <ErrorText>{requestFailedErrorMessage}</ErrorText>
          {enableVoucher && (
            <ErrorText>
              {formatMessage(
                { id: 'tryingToEnterPromoCode' },
                {
                  below: <a href={`#${InputId.PROMO}`}>{formatMessage({ id: 'below' })}</a>,
                }
              )}
            </ErrorText>
          )}
        </>
      )}
      {addVoucherError && !showVoucherErrorText && (
        <ErrorText>
          {formatMessage(addVoucherErrorMessage, {
            above: <a href={`#${InputId.LOYALTY}`}>{formatMessage({ id: 'above' })}</a>,
          })}
        </ErrorText>
      )}
      <Box width="100%" margin="1.5rem 0 0" center>
        <ActionButton
          isLoading={mergeLoyaltyCardsLoading || addVoucherLoading}
          onClick={handleMergeLoyaltyCards}
        >
          {buttonText || formatMessage({ id: 'linkMyCard' })}
        </ActionButton>
      </Box>
    </Container>
  );
};

export default Registration;
